import { Link } from "react-router-dom";

import type { DatasetDashboardType, ShipmentSummary } from "../types";

type Props = {
  shipments: ShipmentSummary[];
  highlightedIds?: string[] | null;
  clientId: string;
  datasetId: string;
  dashboardType?: DatasetDashboardType;
  hasCity?: boolean | null;
  hasBoxes?: boolean;
};

export function ShipmentsTable({
  shipments,
  highlightedIds,
  clientId,
  datasetId,
  dashboardType = "operations",
  hasCity = null,
  hasBoxes = false,
}: Props) {
  if (!shipments.length) {
    return <p>Aucune expédition disponible pour ce dataset.</p>;
  }

  const highlightSet = new Set(highlightedIds ?? []);
  const showTemperatureColumns = dashboardType === "temperature";
  const showCityColumns = hasCity !== false;
  const showBoxesColumn = hasBoxes;

  return (
    <div className="panel-table-wrapper">
      <table className={`panel-table${showTemperatureColumns ? " panel-table--temperature" : ""}`}>
        <thead>
          <tr>
            <th>Consignments</th>
            {showCityColumns ? <th>Destination City</th> : null}
            <th>Destination Country</th>
            {showCityColumns ? <th>Origin City</th> : null}
            <th>Origin Country</th>
            {showBoxesColumn ? <th>Boîtes</th> : null}
            {showTemperatureColumns ? (
              <>
                <th className="panel-col--time-begin">Time Begin</th>
                <th className="panel-col--device">Device</th>
                <th className="panel-col--position">Position</th>
                <th className="panel-col--numeric">Total duration (h)</th>
                <th className="panel-col--numeric">Total Duration Excursion (h)</th>
                <th className="panel-col--numeric">First Excursion Date (h)</th>
              </>
            ) : (
              <>
                <th>Events</th>
                <th>First Event</th>
                <th>Last Event</th>
                <th>Last Status</th>
                <th>Last Location</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {shipments.map((shipment) => (
            <tr key={shipment.id} className={highlightSet.has(shipment.id) ? "highlight-row" : undefined}>
              <td>
                <div className="shipment-cell">
                  <Link
                    className="shipment-id"
                    to={`/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(datasetId)}/consignments/${encodeURIComponent(shipment.id)}`}
                  >
                    {shipment.id}
                  </Link>
                  {shipment.awbNumber ? <span className="shipment-awb">AWB : {shipment.awbNumber}</span> : null}
                  {showTemperatureColumns ? (
                    <span className="status-chip" data-status={shipment.compliance.toLowerCase()}>
                      {shipment.compliance === "YES" ? "Compliant" : "Non compliant"}
                    </span>
                  ) : null}
                </div>
              </td>
              {showCityColumns ? <td>{shipment.destinationCity ?? "—"}</td> : null}
              <td>{shipment.destinationCountry ?? "—"}</td>
              {showCityColumns ? <td>{shipment.originCity ?? "—"}</td> : null}
              <td>{shipment.originCountry ?? "—"}</td>
              {showBoxesColumn ? <td>{renderList(splitDistinctBoxIdentifiers(shipment.boxIdentifier))}</td> : null}
              {showTemperatureColumns ? (
                <>
                  <td className="panel-col--time-begin">
                    {renderList(
                      shipment.devices.map((device) => formatDateTimeCompact(device.timeBegin)),
                      "timeline--compact",
                    )}
                  </td>
                  <td className="panel-col--device">
                    {renderList(
                      shipment.devices.map((device) => formatDeviceIdentifier(shipment.id, device.deviceId)),
                    )}
                  </td>
                  <td className="panel-col--position">
                    {renderList(shipment.devices.map((device) => device.position ?? "—"))}
                  </td>
                  <td className="panel-col--numeric">
                    {renderList(shipment.devices.map((device) => formatNumber(device.totalHours)), "timeline--compact")}
                  </td>
                  <td className="panel-col--numeric">
                    {renderList(
                      shipment.devices.map((device) => formatNumber(device.totalExcursionHours)),
                      "timeline--compact",
                    )}
                  </td>
                  <td className="panel-col--numeric">
                    {renderList(
                      shipment.devices.map((device) => formatNumber(device.firstExcursionHours)),
                      "timeline--compact",
                    )}
                  </td>
                </>
              ) : (
                <>
                  <td>{shipment.eventCount}</td>
                  <td>{formatDate(shipment.firstEvent) ?? "—"}</td>
                  <td>{formatDate(shipment.lastEvent) ?? "—"}</td>
                  <td>{shipment.lastEventType ?? "—"}</td>
                  <td>{shipment.lastLocation ?? "—"}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderList(values: Array<string | null | undefined>, className?: string) {
  const filtered = values.filter((value): value is string => Boolean(value && value.trim().length));
  if (!filtered.length) return <span>—</span>;

  return (
    <ul className={className ? `timeline ${className}` : "timeline"}>
      {filtered.map((value, index) => (
        <li key={`${value}-${index}`}>{value}</li>
      ))}
    </ul>
  );
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString("fr-FR");
  } catch (error) {
    void error;
    return value;
  }
}

function formatNumber(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return value.toFixed(2);
}

function formatDateTimeCompact(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.toLocaleDateString("fr-FR")}\u00A0${parsed.toLocaleTimeString("fr-FR")}`;
}

function splitDistinctBoxIdentifiers(value: string | null | undefined): string[] {
  if (!value) return [];
  const result: string[] = [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const trimmed = raw.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function formatDeviceIdentifier(shipmentId: string, rawDeviceId: string | null | undefined): string {
  const normalized = (rawDeviceId ?? "").trim();
  if (!normalized) return "—";
  const prefix = `${shipmentId}-`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length) || normalized;
  }
  return normalized;
}
